/**
 * The client half of `POST /api/v1/tickets/:id/move-job` (api#771).
 *
 * WHAT A MOVE IS, because the UI has to say it and get it right: ONE column,
 * `tickets.job_id`, plus a version bump. Line items and their prices, the crew
 * snapshot, the ticket's own well/rig/AFE columns and `actual_metres_run` all
 * stay exactly as entered. It is NOT a re-price and NOT a re-snapshot. Where
 * the ticket's own value and the destination job disagree, only a person knows
 * which is right, so the API refuses rather than fixes up — which is why most
 * of this file is about rendering refusals.
 *
 * WHY A `reason` IS REQUIRED AND NOT A PLACEHOLDER: a move leaves no other
 * trace. `job_id` afterwards is indistinguishable from a ticket that was always
 * on that job, so the audit row is the only record and the reason is the only
 * thing on it a human wrote.
 *
 * EVERY CODE HERE WAS READ OFF `fieldiq-api/src/lib/ticket-move-job.ts` AND THE
 * HANDLER IN `tickets.routes.ts`, not off the PR prose. Branch on `code`, never
 * on `error` — the text is written to be shown to the office verbatim and is
 * free to change.
 */

import type {
  MoveJobSnapshotConflict,
  MoveJobPayPeriod,
  MoveJobRateSheet,
  MoveJobCustomerMismatch,
} from '../types/ticket-move-job';

/** Refusal codes. `INVALID_BODY` is a 400; the rest are 409 except the 404. */
export const MOVE_JOB_ERROR_CODES = {
  invalidBody: 'INVALID_BODY',
  ticketFrozen: 'TICKET_FROZEN',
  invoiceLinked: 'TICKET_INVOICE_LINKED',
  destinationNotFound: 'DESTINATION_JOB_NOT_FOUND',
  sameJob: 'SAME_JOB',
  customerMismatch: 'CUSTOMER_MISMATCH',
  payPeriodNotOpen: 'PAY_PERIOD_NOT_OPEN',
  numberDerivedFromJob: 'TICKET_NUMBER_DERIVED_FROM_JOB',
  rateSheetDiffers: 'RATE_SHEET_DIFFERS',
  snapshotConflict: 'SNAPSHOT_CONFLICT',
  // api#830: the ticket records per-string metres against its current job's
  // casing strings, which are not the destination's strings.
  stringMetresRecorded: 'STRING_METRES_RECORDED',
} as const;

export type MoveJobErrorCode =
  (typeof MOVE_JOB_ERROR_CODES)[keyof typeof MOVE_JOB_ERROR_CODES];

/**
 * Warning codes. These ride a **200** — every one is something the office must
 * look at, and none of them is a failure. A warning rendered as an error would
 * make a move that happened read as a move that did not.
 */
export const MOVE_JOB_WARNING_CODES = {
  customerNotes: 'CUSTOMER_NOTES_NEED_REVIEW',
  destinationCompleted: 'DESTINATION_JOB_COMPLETED',
  crewDelta: 'TICKET_WRITER_CREW_CHANGED',
  officeApprovalReverted: 'OFFICE_APPROVAL_REVERTED',
} as const;

export type MoveJobWarningCode =
  (typeof MOVE_JOB_WARNING_CODES)[keyof typeof MOVE_JOB_WARNING_CODES];

/**
 * The payload shapes live in `src/types/ticket-move-job.ts` — re-exported here
 * so a call site needs one import, not two. The split exists because the lint
 * ratchet requires a `src/types/` home for a hand-written response type.
 */
export type {
  MoveJobWarning,
  MoveJobSnapshotConflict,
  MoveJobPayPeriod,
  MoveJobRateSheet,
  MoveJobCustomerMismatch,
  MoveJobResult,
  MoveJobResponse,
} from '../types/ticket-move-job';
/**
 * A refusal, already separated from "the request never arrived".
 *
 * `code` is `null` when the body carried none — a 404 `{error:'Not found'}`, a
 * 403 from `requireRole`, a 500, or a dropped connection. THAT IS A DIFFERENT
 * FACT FROM ANY LISTED CODE and must not be coerced into one: a caller that
 * defaulted an absent code to, say, `TICKET_FROZEN` would tell the office the
 * ticket is frozen when the truth is that nothing was reached.
 */
export interface MoveJobRefusal {
  code: MoveJobErrorCode | string | null;
  status: number | null;
  message: string;
  customer?: MoveJobCustomerMismatch;
  rateSheet?: MoveJobRateSheet;
  payPeriods?: MoveJobPayPeriod[];
  conflicts?: MoveJobSnapshotConflict[];
}

/**
 * Read an axios error into a refusal.
 *
 * The fallback message is deliberately about REACHING the server, not about the
 * move: with no body there is nothing that says the move was refused, only that
 * no answer came back. Saying "this ticket cannot be moved" there would be an
 * answer the screen does not have.
 */
export function moveJobRefusalOf(err: any): MoveJobRefusal {
  const status: number | null = err?.response?.status ?? null;
  const body = err?.response?.data;
  const hasBody = !!body && typeof body === 'object';
  const code = hasBody && typeof body.code === 'string' ? body.code : null;
  const message =
    hasBody && typeof body.error === 'string' && body.error.trim() !== ''
      ? body.error
      : status === 403
        ? 'Your role does not have permission to move a ticket to another job.'
        : status === 404
          ? 'This ticket could not be found.'
          : 'The move could not be completed — the server could not be reached, or it failed before answering.';
  return {
    code,
    status,
    message,
    customer: hasBody && body.customer ? body.customer : undefined,
    rateSheet: hasBody && body.rateSheet ? body.rateSheet : undefined,
    payPeriods: hasBody && Array.isArray(body.payPeriods) ? body.payPeriods : undefined,
    conflicts: hasBody && Array.isArray(body.conflicts) ? body.conflicts : undefined,
  };
}

/**
 * RATE_SHEET_DIFFERS IS THE ONLY RECOVERABLE REFUSAL, and this is the one place
 * that says so. Every other code is terminal for this attempt: re-posting the
 * identical request returns the identical refusal, so an offered retry would be
 * a control that cannot work.
 */
export function isAcknowledgeable(refusal: MoveJobRefusal): boolean {
  return refusal.code === MOVE_JOB_ERROR_CODES.rateSheetDiffers;
}

/**
 * Whether this tenant's ticket numbers are derived from the parent job.
 *
 * In `derived_from_job` mode the number IS the old job's — FT-4290-2 on job
 * J-4290 — so a move would leave a number naming the wrong job and the API
 * refuses for the whole tenant. There is nothing an office user can do about it
 * from this screen, so the button is not rendered at all rather than rendered
 * to produce a 409 (api#771: "best handled by not showing the button").
 *
 * The mode is read from `GET /settings/field-tickets`, which is ungated.
 * UNKNOWN IS NOT `sequential`: while the setting has not arrived this returns
 * `null`, and the caller hides the button rather than offering one it cannot
 * yet justify.
 */
export function ticketNumberModeIsDerived(mode: unknown): boolean | null {
  if (mode === 'derived_from_job') return true;
  if (mode === 'sequential') return false;
  return null;
}

/**
 * The statuses the API freezes (`FROZEN_STATUSES`, guard 1). Hiding the button
 * on these is not a substitute for the guard — it is so the office is not
 * offered an action the API will refuse.
 */
export const MOVE_JOB_FROZEN_STATUSES = ['customer_approved', 'invoiced'] as const;

export function moveJobIsFrozen(status: string | undefined | null): boolean {
  return (MOVE_JOB_FROZEN_STATUSES as readonly string[]).includes(status ?? '');
}

/**
 * EXACTLY the API's gate: `requireRole('admin', 'office_staff')`.
 *
 * NOT `canApproveTickets`, which also admits `supervisor` and `owner` — those
 * two would be offered the button and handed a 403. A move changes revenue
 * between jobs and changes who may delete the ticket, which is why the API
 * scoped it narrower than approval.
 *
 * A `dispatcher` at a tenant carrying the admin-elevation flag would in fact
 * pass the API's non-strict gate. The button stays hidden for them anyway,
 * because api#771 states the intent as "no dispatcher"; widening it is a
 * decision for that repo, not a guess here.
 */
export function canMoveTicketJob(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'office_staff';
}
